import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { applyBrand, onColour, unusable } from "../lib/brand";
import { LogoUpload } from "../components/LogoUpload";
import type { Company } from "../lib/access";

export function Settings({ company, onSaved }: { company: Company; onSaved: () => void }) {
  const [displayName, setDisplayName] = useState(company.displayName ?? company.name);
  const [timezone, setTimezone] = useState(company.timezone ?? "UTC");
  // The three logo keys, kept separate from the form state above: uploads land
  // immediately rather than on save, so they must not be lost if the form is
  // abandoned.
  const [assets, setAssets] = useState<{
    wordmark_key: string | null;
    wordmark_reverse_key: string | null;
    icon_key: string | null;
  }>({ wordmark_key: null, wordmark_reverse_key: null, icon_key: null });

  const reloadAssets = useCallback(async () => {
    const { data } = await supabase
      .from("companies")
      .select("wordmark_key,wordmark_reverse_key,icon_key")
      .eq("id", company.id)
      .single();
    if (data) setAssets(data as typeof assets);
  }, [company.id]);

  useEffect(() => { void reloadAssets(); }, [reloadAssets]);

  const [hex, setHex] = useState(company.brandHex ?? "#225333");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const bad = unusable(hex);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (bad) return;
    setBusy(true);
    setMsg(null);
    const { error } = await supabase.rpc("update_company_identity", {
      p_company: company.id,
      p_display_name: displayName,
      p_timezone: timezone,
      p_brand_hex: hex,
    });
    setBusy(false);
    if (error) return setMsg(error.message);
    applyBrand(hex);
    setMsg("Saved. Your team will see this the next time they load the console.");
    onSaved();
  }

  return (
    <>
      <header className="page-head">
        <h1>Company settings</h1>
        <p className="muted">
          What your own people see when they sign in. This does not change the legal name
          on your invoices.
        </p>
      </header>

      <form className="card" onSubmit={save}>
        <label htmlFor="dn">Workspace name</label>
        <input id="dn" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />

        <label htmlFor="tz">Timezone</label>
        <input id="tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}
          placeholder="Africa/Nairobi" />

        <label htmlFor="bc">Brand colour</label>
        <div className="colour-row">
          <input id="bc" type="color" value={hex} onChange={(e) => setHex(e.target.value)} />
          <input aria-label="Brand colour hex" value={hex}
            onChange={(e) => setHex(e.target.value)} spellCheck={false} />
          <span className="swatch" style={{ background: hex, color: onColour(hex) }}>
            Button
          </span>
        </div>
        {bad ? (
          <p className="status error">
            Neither black nor white text is readable on this colour. Pick something darker
            or lighter — buttons using it would be hard to read.
          </p>
        ) : (
          <p className="muted small">
            Text on this colour will be {onColour(hex) === "#FFFFFF" ? "white" : "black"},
            chosen automatically so it stays readable.
          </p>
        )}

        <button className="btn" type="submit" disabled={busy || bad}>
          {busy ? "Saving…" : "Save settings"}
        </button>
        {msg && <p className="status">{msg}</p>}
      </form>

      <Retention company={company} onSaved={onSaved} />
    
      <section className="card">
        <h2>Your logo</h2>
        <p className="muted">
          What your team sees when they sign in. Transparent backgrounds only — a JPEG
          arrives as a white rectangle sitting on the sidebar.
        </p>
        <LogoUpload companyId={company.id} kind="wordmark"
          currentKey={assets.wordmark_key} onDone={reloadAssets} />
        <LogoUpload companyId={company.id} kind="wordmark_reverse"
          currentKey={assets.wordmark_reverse_key} onDone={reloadAssets} />
        <LogoUpload companyId={company.id} kind="icon"
          currentKey={assets.icon_key} onDone={reloadAssets} />
      </section>
    </>
  );
}

/**
 * How long recordings are kept.
 *
 * Deliberately its own form and its own save. Retention is not a preference
 * like a brand colour — shortening it destroys evidence and lengthening it
 * creates liability — so it should not ride along with a click on "Save
 * settings" meant for something else.
 */
function Retention({ company, onSaved }: { company: Company; onSaved: () => void }) {
  const [days, setDays] = useState<string>(
    company.retentionDays === null ? "forever" : String(company.retentionDays),
  );
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const changed = days !== (company.retentionDays === null ? "forever" : String(company.retentionDays));

  async function save() {
    setBusy(true);
    setMsg(null);
    const { error } = await supabase.rpc("set_retention", {
      p_company: company.id,
      p_days: days === "forever" ? null : Number(days),
    });
    setBusy(false);
    if (error) return setMsg(error.message);
    setMsg("Saved. This applies from tonight's clean-up onwards.");
    onSaved();
  }

  return (
    <div className="card">
      <h2>How long recordings are kept</h2>
      <p className="muted small">
        Audio is deleted automatically once it reaches this age, and the verbatim
        transcript goes with it. Notes and summaries on the call are kept either way.
      </p>

      <label htmlFor="rt">Keep recordings for</label>
      <select id="rt" value={days} onChange={(e) => setDays(e.target.value)}>
        <option value="90">90 days — the default</option>
        <option value="365">365 days</option>
        <option value="forever">Indefinitely</option>
      </select>

      {days === "forever" && (
        <p className="muted small">
          Nothing is deleted on a schedule. Recorded voices are personal data, so this
          is worth choosing on purpose rather than because it is the easiest option.
        </p>
      )}

      {changed && Number(days) < Number(company.retentionDays ?? Infinity) && (
        <p className="status error">
          This is shorter than your current setting. Recordings already older than the
          new limit are deleted at tonight's clean-up and cannot be recovered.
        </p>
      )}

      <button className="btn" type="button" disabled={busy || !changed} onClick={() => void save()}>
        {busy ? "Saving…" : "Save retention"}
      </button>
      {msg && <p className="status">{msg}</p>}
    </div>
  );
}
